/**
 * ChangeSetApprovalMessage — the ONE card for an agent change set
 * (``doc_propose_changes``, bridge input_type ``change_set_approval``).
 *
 * Each change gets a row: its id, section, rationale and a word diff, with a
 * tick box. Clicking a row's header jumps Word to the text it changes. The
 * writer answers once — ``{approved_ids: [...]}`` for "Apply selected",
 * ``{approved: false}`` for "Reject all" — and the agent's tool call
 * returns with every change's outcome.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useMemo, useState } from "react";
import type { UserInputRequest } from "../../services/documentBridge";
import { navigateToSection, selectSpanInSection } from "../../services/wordWriter";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";
import AwaitingDecisionFrame from "./AwaitingDecisionFrame";
import { DiffView } from "./DiffView";

export interface ChangeSetChange {
  id: string;
  section: string;
  op: "replace" | "delete" | "insert_after" | "insert_before";
  before: string;
  after: string;
  rationale?: string;
}

export interface ChangeSetPayload {
  reason?: string;
  changes: ChangeSetChange[];
}

/** The JSON block the ChangeSetGate puts at the end of the question. */
export function parseChangeSetPayload(question: string): ChangeSetPayload | null {
  const match = /\{[\s\S]*\}/u.exec(question);
  if (!match) return null;
  try {
    const obj = JSON.parse(match[0]);
    if (obj && Array.isArray(obj.changes) && obj.changes.every((c: ChangeSetChange) => typeof c?.id === "string")) {
      return obj as ChangeSetPayload;
    }
  } catch {
    // fall through
  }
  return null;
}

export const OP_LABEL: Record<ChangeSetChange["op"], string> = {
  replace: "Replace",
  delete: "Delete",
  insert_after: "Insert after",
  insert_before: "Insert before",
};

const useStyles = makeStyles({
  card: {
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "10px",
    background: "var(--surface-raised)",
    padding: "10px 12px",
    fontFamily: fonts.body,
    // The frame is a shrink-to-fit flex column: without a fixed width the
    // card grows to its widest no-wrap line (the rationale) and runs off
    // the task pane, hiding Apply behind a horizontal scroll.
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },
  header: { fontSize: "12.5px", fontWeight: 600, color: inkColors.ink, marginBottom: "2px" },
  reason: { fontSize: "11.5px", color: inkColors.ink3, marginBottom: "8px" },
  row: {
    borderTop: `1px solid ${surfaceColors.line}`,
    padding: "8px 0",
  },
  rowOff: { opacity: 0.55 },
  rowHead: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" },
  jump: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flex: 1,
    minWidth: 0,
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    textAlign: "left",
    color: inkColors.ink2,
    fontSize: "11.5px",
    ":hover": { color: brandColors.primary },
  },
  id: {
    fontFamily: fonts.mono,
    fontSize: "10.5px",
    fontWeight: 600,
    color: brandColors.primary,
    background: brandColors.light,
    borderRadius: "4px",
    padding: "1px 5px",
  },
  meta: { fontFamily: fonts.mono, fontSize: "10.5px", color: inkColors.ink4, whiteSpace: "nowrap" },
  rationale: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  actions: { display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "10px" },
  approveBtn: {
    border: "none",
    borderRadius: "8px",
    padding: "6px 14px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: brandColors.primary,
    color: "var(--on-brand)",
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
  rejectBtn: {
    borderRadius: "8px",
    padding: "6px 14px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    background: "var(--surface-raised)",
    color: inkColors.ink2,
    border: `1px solid ${surfaceColors.lineStrong}`,
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
  warning: { fontSize: "11px", color: "var(--danger)", marginTop: "6px" },
});

export interface ChangeSetApprovalMessageProps {
  request: UserInputRequest;
  bridgeConnected: boolean;
  onSubmit: (value: string) => void;
}

export const ChangeSetApprovalMessage: React.FC<ChangeSetApprovalMessageProps> = ({
  request,
  bridgeConnected,
  onSubmit,
}) => {
  const styles = useStyles();
  const payload = useMemo(() => parseChangeSetPayload(request.question), [request.question]);
  const [unticked, setUnticked] = useState<Set<string>>(() => new Set());
  if (!payload) return null;

  const picked = payload.changes.filter((c) => !unticked.has(c.id)).map((c) => c.id);
  const toggle = (id: string) =>
    setUnticked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const reveal = (c: ChangeSetChange) => {
    void selectSpanInSection(c.section, c.before).then((found) => {
      if (!found) void navigateToSection(c.section);
    });
  };
  const n = payload.changes.length;

  return (
    <AwaitingDecisionFrame show={bridgeConnected} label="Awaiting your approval · only the ticked changes are applied">
      <div className={styles.card}>
        <div className={styles.header}>
          {n} proposed change{n === 1 ? "" : "s"}
        </div>
        {payload.reason ? <div className={styles.reason}>{payload.reason}</div> : null}
        {payload.changes.map((c) => {
          const on = !unticked.has(c.id);
          return (
            <div key={c.id} className={mergeClasses(styles.row, !on && styles.rowOff)}>
              <div className={styles.rowHead}>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggle(c.id)}
                  aria-label={`Apply ${c.id}`}
                  disabled={!bridgeConnected}
                />
                <button type="button" className={styles.jump} onClick={() => reveal(c)} title="Show in Word">
                  <span className={styles.id}>{c.id}</span>
                  <span className={styles.meta}>
                    §{c.section} · {OP_LABEL[c.op] ?? c.op}
                  </span>
                  {c.rationale ? <span className={styles.rationale}>{c.rationale}</span> : null}
                </button>
              </div>
              <DiffView before={c.before} after={c.after} maxHeight={140} hideSummary />
            </div>
          );
        })}
        {bridgeConnected ? null : (
          <div className={styles.warning}>Connection to the agent was lost — this request can no longer be answered.</div>
        )}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.rejectBtn}
            disabled={!bridgeConnected}
            onClick={() => onSubmit(JSON.stringify({ approved: false }))}
          >
            Reject all
          </button>
          <button
            type="button"
            className={styles.approveBtn}
            disabled={!bridgeConnected || picked.length === 0}
            onClick={() => onSubmit(JSON.stringify({ approved_ids: picked }))}
          >
            {picked.length === n ? `Apply all ${n}` : `Apply ${picked.length} selected`}
          </button>
        </div>
      </div>
    </AwaitingDecisionFrame>
  );
};

export default ChangeSetApprovalMessage;
